import { useState, useEffect, useRef } from 'react';
import { X, Mail, Clock, Info, ExternalLink, Plus } from 'lucide-react';
import Avatar from './Avatar';

const UserProfileModal = ({ user, onClose, onAddBirthday }) => {
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const startPos = useRef({ x: 0, y: 0 });

  const handleMouseDown = (e) => {
    if (e.target.closest('button')) return;
    setIsDragging(true);
    startPos.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
  };

  useEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (e) => {
      setPos({ x: e.clientX - startPos.current.x, y: e.clientY - startPos.current.y });
    };
    const handleMouseUp = () => setIsDragging(false);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const handleBackdropWheel = (e) => {
    const calendar = document.querySelector('.custom-scrollbar');
    if (calendar) {
      calendar.scrollTop += e.deltaY;
    }
  };

  if (!user) return null;

  const formatLastSeen = (ts) => {
    if (!ts) return 'Never';
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-zinc-900/10 animate-in fade-in duration-300"
        onClick={onClose}
        onWheel={handleBackdropWheel}
      />

      {/* Modal */}
      <div
        data-testid="user-profile-modal"
        className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 pointer-events-auto cursor-grab active:cursor-grabbing"
        style={{ transform: `translate(${pos.x}px, ${pos.y}px)` }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={handleMouseDown}
      >
        {/* Header/Banner Area */}
        <div className="h-24 bg-zinc-100 flex justify-end p-4">
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/80 flex items-center justify-center text-zinc-500 hover:text-zinc-900 shadow-sm transition-colors"
          >
            <X size={18} strokeWidth={2.5} />
          </button>
        </div>

        {/* Profile Content */}
        <div className="px-8 pb-8 -mt-12">
          <div className="flex flex-col items-center text-center">
            <div className="p-1.5 bg-white rounded-[2rem] shadow-xl mb-4">
              <Avatar src={user.avatar} size={80} className="rounded-[1.6rem]" />
            </div>

            <h2 className="text-2xl font-black text-zinc-900 tracking-tight mb-1">{user.name}</h2>
            <div className="flex items-center gap-1 text-zinc-400 mb-6">
              <Mail size={12} strokeWidth={2.5} />
              <span className="text-[10px] font-bold uppercase tracking-widest">
                {user.email || 'No email provided'}
              </span>
            </div>

            {/* Chat Link */}
            <button
              onClick={() => {
                window.open(`/p/${user.id}/chat` + window.location.search, '_blank');
              }}
              className="mb-6 flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-black transition-all active:scale-95 shadow-lg shadow-zinc-200"
            >
              Open Chat in New Tab
            </button>

            <button
              onClick={() => {
                window.open(`/social?user=${user.id}`, '_blank');
              }}
              className="mb-6 flex items-center gap-2 px-4 py-2 bg-white border border-zinc-200 text-zinc-600 hover:text-zinc-900 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-zinc-50 transition-all active:scale-95 shadow-sm"
            >
              <ExternalLink size={14} strokeWidth={2.5} />
              View Social Feed
            </button>

            {/* Bio Section */}
            <div className="w-full bg-zinc-50 rounded-2xl p-4 mb-6 text-left border border-zinc-100">
              <div className="flex items-center gap-2 mb-2 text-zinc-400">
                <Info size={14} strokeWidth={2.5} />
                <span className="text-[10px] font-black uppercase tracking-widest">About</span>
              </div>
              <p className="text-sm text-zinc-600 leading-relaxed italic">
                {user.bio || "This user hasn't set a bio yet."}
              </p>
            </div>

            {/* Stats/Info */}
            <div className="w-full flex items-center justify-center gap-2 text-zinc-400">
              <Clock size={12} strokeWidth={2.5} />
              <span className="text-[10px] font-bold uppercase tracking-widest">
                Last seen {formatLastSeen(user.lastSeen)}
              </span>
            </div>

            {user.birthday && (
              <div className="w-full flex items-center justify-center gap-2 mt-4 text-zinc-500">
                <span className="text-xs font-bold bg-zinc-100 px-3 py-1.5 rounded-full flex items-center gap-2">
                  🎂 Birthday: {user.birthday}
                  {user.birthYear && ` (${new Date().getFullYear() - parseInt(user.birthYear)} yrs)`}
                  <button 
                    onClick={() => {
                      if (onAddBirthday) onAddBirthday(user);
                    }}
                    className="ml-1 w-5 h-5 bg-white border border-zinc-200 rounded-full flex items-center justify-center hover:bg-zinc-900 hover:text-white hover:border-zinc-900 transition-colors shadow-sm"
                    title="Add to Calendar"
                  >
                    <Plus size={12} strokeWidth={3} />
                  </button>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileModal;
